import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Typography from "@mui/material/Typography";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

import Langs, { Localize } from "../../app/lang/langs";
import { showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import {
    deleteVLearnCategory, getVLearnSettings, listVLearnCategories,
    saveVLearnCategory, updateVLearnSettings
} from "./vlearnSlice";
import {
    VLearnEmptyState, vlearnSurface, vlearnType
} from "../../components/vlearn/vlearnUi";

/**
 * The numeric settings' permitted ranges, mirroring `SETTINGS_SPEC` in the backend's
 * `helpers/vlearn.js`.
 *
 * `validateSetting` refuses anything outside these bounds with a 422, so a form that let a
 * value through would be a form whose Save button fails for a reason the user has to guess.
 * A range widened there and not here produces a field that refuses a value the server would
 * have taken — annoying but safe; narrowed there and not here produces the 422, which is why
 * the helper text quotes these numbers rather than describing them vaguely.
 */
const NUMERIC_SPEC = {
    // `unit` decides how the bounds and the current value are described. A raw bound is
    // meaningless to a reader — "Between 30 and 3650" tells them nothing, whereas "Between
    // 30 days and 10 years" is the fact they need. Must stay in step with SETTINGS_SPEC in
    // the backend's helpers/vlearn.js, which is what actually enforces the range.
    max_video_upload_mb: { min: 1, max: 5000, unit: 'mb' },
    max_document_upload_mb: { min: 1, max: 500, unit: 'mb' },
    activity_log_retention_days: { min: 30, max: 3650, unit: 'days' }
}

/**
 * Describes a day count in the largest unit that divides it cleanly.
 *
 * 3650 becomes "10 years", 365 "1 year", 90 "3 months", 45 "45 days". A reader setting a
 * retention period thinks in years and months; the column stores days, and that is an
 * implementation detail they should not have to convert in their head.
 */
function describeDays(value, lang) {
    const days = Number(value)
    if (!Number.isFinite(days) || days <= 0) return null

    if (days % 365 === 0) {
        const years = days / 365
        return `${years} ${Localize(years === 1 ? 'VLEARN_UNIT_YEAR_ONE' : 'VLEARN_UNIT_YEAR_MANY', lang)}`
    }
    if (days % 30 === 0) {
        const months = days / 30
        return `${months} ${Localize(months === 1 ? 'VLEARN_UNIT_MONTH_ONE' : 'VLEARN_UNIT_MONTH_MANY', lang)}`
    }
    // Not a clean multiple: give the exact days plus the nearest year, so a hand-typed 400
    // still reads as "just over a year" rather than as an opaque number.
    const exact = `${days} ${Localize(days === 1 ? 'VLEARN_UNIT_DAY_ONE' : 'VLEARN_UNIT_DAY_MANY', lang)}`
    if (days < 365) return exact
    const years = Math.round((days / 365) * 10) / 10
    return `${exact} (~${years} ${Localize(years === 1 ? 'VLEARN_UNIT_YEAR_ONE' : 'VLEARN_UNIT_YEAR_MANY', lang)})`
}

/** Megabytes, promoted to GB once the number stops being easy to read. */
function describeMegabytes(value, lang) {
    const mb = Number(value)
    if (!Number.isFinite(mb) || mb <= 0) return null
    if (mb >= 1000 && mb % 1000 === 0) {
        return `${mb / 1000} ${Localize('VLEARN_UNIT_GB', lang)}`
    }
    return `${mb} ${Localize('VLEARN_UNIT_MB', lang)}`
}

/** The description for one numeric setting's value, in its own unit. */
function describeValue(unit, value, lang) {
    return unit === 'days' ? describeDays(value, lang) : describeMegabytes(value, lang)
}

/**
 * Describes a BOUND, which is a different job from describing a value.
 *
 * A bound is a constraint on what the user types, so it has to be stated in the unit the
 * field is in — the label already says "(days)" / "(MB)", and quoting the maximum as
 * "10 years" would leave them guessing what number to enter. But "3650" alone was the
 * complaint that prompted this: opaque, and nobody should have to divide by 365.
 *
 * So: the exact figure they would type, plus the human reading in brackets when there is a
 * cleaner one. "3650 days (10 years)". The minimum usually needs no gloss.
 */
function describeBound(unit, value, lang) {
    const raw = Number(value)
    const unitWord = unit === 'days'
        ? Localize(raw === 1 ? 'VLEARN_UNIT_DAY_ONE' : 'VLEARN_UNIT_DAY_MANY', lang)
        : Localize('VLEARN_UNIT_MB', lang)
    const exact = `${raw} ${unitWord}`

    const friendly = describeValue(unit, raw, lang)
    // Only add the gloss when it says something the exact figure does not. `describeDays`
    // returns "30 days" unchanged for a value with no cleaner reading, and there is no point
    // rendering "30 days (30 days)".
    return friendly && friendly !== exact ? `${exact} (${friendly})` : exact
}

/** The `video_source` enum, in the order the radio group offers it. */
const VIDEO_SOURCES = [
    { value: 'youtube', labelKey: 'VLEARN_VIDEO_SOURCE_YOUTUBE', helpKey: 'VLEARN_VIDEO_SOURCE_YOUTUBE_HELP' },
    { value: 'upload', labelKey: 'VLEARN_VIDEO_SOURCE_UPLOAD', helpKey: 'VLEARN_VIDEO_SOURCE_UPLOAD_HELP' },
    { value: 'both', labelKey: 'VLEARN_VIDEO_SOURCE_BOTH', helpKey: 'VLEARN_VIDEO_SOURCE_BOTH_HELP' }
]

/** A small, fixed palette for category colours, so the catalogue does not become a rainbow. */
const CATEGORY_COLORS = [
    '#FF9015', '#3A9C60', '#1179BE', '#8E4EC6', '#C4302B', '#D97706', '#4B5263', '#0F766E'
]

/**
 * The server's own message for a failed call, or null.
 *
 * `postRequest` resolves `{ responseError: error.response }` — the axios response — so a
 * 422's body is at `.data.error`. That sentence names the setting and its range; a generic
 * "could not save" makes the admin guess which of six fields the server objected to.
 */
const serverError = (payload) => (payload
    && payload.responseError
    && payload.responseError.data
    && payload.responseError.data.error) || null

/**
 * V-Learning module settings — administrators only.
 *
 * `VLearnController.updateSettings` refuses anyone whose `roleId` is above 1, and does so
 * because these values decide what the company's own disk accepts and how long employee
 * activity is retained — decisions about infrastructure and data protection, not about
 * teaching. `state.vlearn.reach.isAdmin` is the server's own answer to the same question,
 * taken from `getSettings` rather than recomputed here, so the screen and the endpoint cannot
 * disagree about who may act.
 *
 * The category manager sits on this screen but answers to a different rule: `saveCategory`
 * and `deleteCategory` require `edit_all`, because the category list is company-wide
 * vocabulary. An admin without that right can read the list and is told why they cannot
 * change it.
 */
export default function VLearnSettings({ onBack }) {

    const dispatch = useDispatch()

    const settings = useSelector((state) => state.vlearn.settings)
    const reach = useSelector((state) => state.vlearn.reach)
    const categories = useSelector((state) => state.vlearn.categories)
    const lang = useSelector((state) => state.settings.lang)

    /** The form's working copy. Numbers are held as strings so a half-typed value survives. */
    const seededSettingsRef = useRef(false)
    const [form, setForm] = useState({
        video_source: 'both',
        max_video_upload_mb: '500',
        max_document_upload_mb: '50',
        activity_log_retention_days: '365',
        certificates_enabled: true,
        announcements_email_enabled: true
    })
    const [saving, setSaving] = useState(false)

    // Keyed rather than a bare boolean so React 18's StrictMode double mount is one request.
    const fetched = useRef(false)

    useEffect(() => {
        if (fetched.current) return
        fetched.current = true
        dispatch(getVLearnSettings({}))
        dispatch(listVLearnCategories({}))
    }, [dispatch])

    // The stored settings are the form's source of truth on arrival and after every save.
    // `coerceSettings` guarantees every key is present, so there is nothing to default here.
    useEffect(() => {
        if (!settings) return
        // Seeded once, not kept in sync. These settings are a singleton, so there is no id to
        // key on — the first payload to arrive wins and later ones are ignored, because the
        // form is the admin's working copy from that point on. Re-seeding on every arrival
        // discarded input typed while a refresh was in flight.
        if (seededSettingsRef.current) return
        seededSettingsRef.current = true
        setForm({
            video_source: settings.video_source,
            max_video_upload_mb: String(settings.max_video_upload_mb),
            max_document_upload_mb: String(settings.max_document_upload_mb),
            activity_log_retention_days: String(settings.activity_log_retention_days),
            certificates_enabled: Boolean(settings.certificates_enabled),
            announcements_email_enabled: Boolean(settings.announcements_email_enabled)
        })
    }, [settings])

    /** Which numeric fields currently hold a value the server would refuse. */
    const numericErrors = useMemo(() => {
        const errors = {}
        for (const [key, spec] of Object.entries(NUMERIC_SPEC)) {
            const parsed = parseInt(form[key], 10)
            if (!Number.isInteger(parsed) || parsed < spec.min || parsed > spec.max) {
                errors[key] = true
            }
        }
        return errors
    }, [form])

    const hasErrors = Object.keys(numericErrors).length > 0

    const onSave = useCallback(async () => {
        if (hasErrors) {
            showErrorToast(Localize("VLEARN_SETTINGS_FIX_ERRORS", lang))
            return
        }
        setSaving(true)
        const response = await dispatch(updateVLearnSettings({
            settings: {
                video_source: form.video_source,
                max_video_upload_mb: parseInt(form.max_video_upload_mb, 10),
                max_document_upload_mb: parseInt(form.max_document_upload_mb, 10),
                activity_log_retention_days: parseInt(form.activity_log_retention_days, 10),
                certificates_enabled: form.certificates_enabled ? 1 : 0,
                announcements_email_enabled: form.announcements_email_enabled ? 1 : 0
            }
        }))
        setSaving(false)
        // Thunks in this slice never reject; a failure arrives on `.fulfilled` carrying
        // `responseError`, so the server's own sentence is what gets shown.
        const payload = response.payload || {}
        const data = payload.responseData
        if (!data || data.status !== 1) {
            showErrorToast(serverError(payload) || Localize("VLEARN_SAVE_FAILED", lang))
            return
        }
        showSuccessToast(Localize("VLEARN_SETTINGS_SAVED", lang))
    }, [dispatch, form, hasErrors, lang])

    // ── Categories ──────────────────────────────────────────────────────────────────

    const canEditCategories = Boolean(reach.editAll)

    const [draftCategory, setDraftCategory] = useState(null)
    const [pendingDeactivate, setPendingDeactivate] = useState(null)

    const onSaveCategory = useCallback(async () => {
        if (!draftCategory) return
        const name = String(draftCategory.name || '').trim()
        if (!name) {
            showErrorToast(Localize("VLEARN_CATEGORY_NAME_REQUIRED", lang))
            return
        }
        const response = await dispatch(saveVLearnCategory({
            id: draftCategory.id || undefined,
            name,
            color: draftCategory.color || undefined,
            order: draftCategory.order
        }))
        const payload = response.payload || {}
        const data = payload.responseData
        if (!data || data.status !== 1) {
            showErrorToast(serverError(payload) || Localize("VLEARN_SAVE_FAILED", lang))
            return
        }
        setDraftCategory(null)
        showSuccessToast(Localize("VLEARN_CATEGORY_SAVED", lang))
        dispatch(listVLearnCategories({}))
    }, [dispatch, draftCategory, lang])

    const onConfirmDeactivate = useCallback(async () => {
        if (!pendingDeactivate) return
        const response = await dispatch(deleteVLearnCategory({ id: pendingDeactivate.id }))
        const payload = response.payload || {}
        const data = payload.responseData
        setPendingDeactivate(null)
        if (!data || data.status !== 1) {
            showErrorToast(serverError(payload) || Localize("VLEARN_SAVE_FAILED", lang))
            return
        }
        showSuccessToast(Localize("VLEARN_CATEGORY_DEACTIVATED", lang))
        dispatch(listVLearnCategories({}))
    }, [dispatch, pendingDeactivate, lang])

    // ── The admin gate ──────────────────────────────────────────────────────────────
    //
    // `reach` arrives with `getSettings`, so a refusal drawn before that response would be a
    // guess at the reader's role. The initial `isAdmin: false` and a genuine non-admin are
    // indistinguishable for the first moment either way; the screen simply says what the
    // server will do, and the server is asked again on every call regardless.
    if (!reach.isAdmin) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ ...vlearnSurface.card }}>
                    <VLearnEmptyState
                        icon={<LockOutlinedIcon />}
                        title={<Langs str="VLEARN_ADMIN_ONLY_TITLE" />}
                        description={<Langs str="VLEARN_ADMIN_ONLY_BODY" />}
                        // The other branch of this same file renders a back button; this one
                        // did not, despite `onBack` being received. A refusal with no exit is
                        // a dead end, and the module's navigation is local state, so the
                        // browser's Back leaves V-Learning entirely.
                        action={onBack ? (
                            <Button variant="outlined" size="small" onClick={onBack}
                                sx={{ textTransform: 'none' }}>
                                <Langs str="VLEARN_BACK" />
                            </Button>
                        ) : null}
                    />
                </Box>
            </Box>
        )
    }

    const numberField = (key, labelKey, extraHelp) => {
        const spec = NUMERIC_SPEC[key]
        const invalid = Boolean(numericErrors[key])
        const currentLabel = describeValue(spec.unit, form[key], lang)
        return (
            <Box sx={{ maxWidth: 420 }}>
                <TextField
                    label={Localize(labelKey, lang)}
                    value={form[key]}
                    onChange={(event) => {
                        // Digits only. Letting other characters in would produce a NaN the
                        // server answers with a 422 about a value the user never typed.
                        const next = event.target.value.replace(/[^0-9]/g, '')
                        setForm((current) => ({ ...current, [key]: next }))
                    }}
                    error={invalid}
                    type="text"
                    size="small"
                    fullWidth
                    inputProps={{
                        inputMode: 'numeric',
                        'aria-describedby': `${key}-help`
                    }}
                />
                <FormHelperText id={`${key}-help`} error={invalid} sx={{ mt: 0.5 }}>
                    {invalid
                        ? <Langs str="VLEARN_SETTINGS_OUT_OF_RANGE" />
                        : null}
                    {invalid ? ' ' : null}
                    {/* Bounds in real units, not raw numbers. "Between 30 and 3650" is
                        the version that prompted this — 3650 is ten years, and nobody
                        should have to work that out. */}
                    <Langs str="VLEARN_SETTING_RANGE_HELP"
                        params={{
                            min: describeBound(spec.unit, spec.min, lang),
                            max: describeBound(spec.unit, spec.max, lang)
                        }} />
                </FormHelperText>
                {/* What the value the user has typed actually means. The field is in days
                    because the column is, so "365" needs to read back as "1 year" while
                    they are still looking at it. Hidden when the value is out of range —
                    the error message is the useful thing then. */}
                {!invalid && currentLabel ? (
                    <FormHelperText sx={{ mt: 0.25, fontWeight: 600 }}>
                        <Langs str="VLEARN_SETTING_CURRENT" params={{ value: currentLabel }} />
                    </FormHelperText>
                ) : null}
                {extraHelp ? (
                    <FormHelperText sx={{ mt: 0.75, lineHeight: 1.5 }}>
                        {extraHelp}
                    </FormHelperText>
                ) : null}
            </Box>
        )
    }

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 3,
            p: { xs: 2, sm: 3 }, maxWidth: 980
        }}>

            {/* ── Header ──────────────────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                    {/* This screen used to rely on VLearnView's header for its back
                        button. That header is gone (it duplicated every screen's own), so
                        the affordance lives here now — without it the settings screen is a
                        dead end, because the module's navigation is local state rather than
                        routes and the browser's Back leaves V-Learning entirely. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {onBack ? (
                            <Tooltip title={Localize('VLEARN_BACK', lang)}>
                                <IconButton
                                    onClick={onBack}
                                    aria-label={Localize('VLEARN_BACK', lang)}
                                    size="small"
                                    // The only way out of the settings screen, and a bordered
                                    // `size="small"` button is 32px square.
                                    sx={{
                                        border: '1px solid rgba(0,0,0,0.10)',
                                        minWidth: 44, minHeight: 44
                                    }}
                                >
                                    <ArrowBackIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        <Typography component="h1" sx={{ ...vlearnType.pageTitle }}>
                            <Langs str="VLEARN_SETTINGS_TITLE" />
                        </Typography>
                    </Box>
                    <Typography sx={{ ...vlearnType.body, mt: 0.25 }}>
                        <Langs str="VLEARN_SETTINGS_SUBTITLE" />
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    size="small"
                    onClick={onSave}
                    disabled={saving || hasErrors}
                    startIcon={<SaveRoundedIcon />}
                    sx={{ textTransform: 'none' }}
                >
                    <Langs str="VLEARN_SAVE" />
                </Button>
            </Box>

            {/* ── The headline setting ────────────────────────────────────────────── */}
            <Box component="section" sx={{ ...vlearnSurface.panel }}>
                <FormControl>
                    <FormLabel
                        component="legend"
                        id="vlearn-video-source-label"
                        sx={{ ...vlearnType.sectionTitle, mb: 0.5, '&.Mui-focused': { color: 'inherit' } }}
                    >
                        <Langs str="VLEARN_SETTING_VIDEO_SOURCE" />
                    </FormLabel>
                    <Typography sx={{ ...vlearnType.body, mb: 1.5 }}>
                        <Langs str="VLEARN_SETTING_VIDEO_SOURCE_HELP" />
                    </Typography>

                    <RadioGroup
                        aria-labelledby="vlearn-video-source-label"
                        value={form.video_source}
                        onChange={(event) =>
                            setForm((current) => ({ ...current, video_source: event.target.value }))}
                    >
                        {VIDEO_SOURCES.map((option) => (
                            <Box
                                key={option.value}
                                sx={{
                                    border: '1px solid',
                                    borderColor: form.video_source === option.value
                                        ? 'var(--orange, rgb(255,144,21))' : 'rgba(0,0,0,0.10)',
                                    backgroundColor: form.video_source === option.value
                                        ? 'rgba(255,144,21,0.05)' : 'transparent',
                                    borderRadius: '10px', px: 1.5, py: 1.25, mb: 1.25
                                }}
                            >
                                <FormControlLabel
                                    value={option.value}
                                    control={<Radio size="small" />}
                                    label={
                                        <Typography sx={{ ...vlearnType.cardTitle }}>
                                            <Langs str={option.labelKey} />
                                        </Typography>
                                    }
                                    sx={{ m: 0 }}
                                />
                                {/*
                                  * Each option states its consequence rather than its name.
                                  * "YouTube only" tells an admin nothing; "no server storage,
                                  * but an external dependency and a video anyone with the link
                                  * can watch" is the decision they are actually making.
                                  */}
                                <Typography sx={{ ...vlearnType.meta, ml: 3.75, lineHeight: 1.5 }}>
                                    <Langs str={option.helpKey} />
                                </Typography>
                            </Box>
                        ))}
                    </RadioGroup>
                </FormControl>
            </Box>

            {/* ── Upload limits ───────────────────────────────────────────────────── */}
            <Box component="section" sx={{ ...vlearnSurface.panel }}>
                <Typography component="h2" sx={{ ...vlearnType.sectionTitle, mb: 2 }}>
                    <Langs str="VLEARN_SETTINGS_UPLOADS_GROUP" />
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {numberField('max_video_upload_mb', 'VLEARN_SETTING_MAX_VIDEO_MB')}
                    {numberField('max_document_upload_mb', 'VLEARN_SETTING_MAX_DOCUMENT_MB')}
                </Box>
            </Box>

            {/* ── Retention ───────────────────────────────────────────────────────── */}
            <Box component="section" sx={{ ...vlearnSurface.panel }}>
                <Typography component="h2" sx={{ ...vlearnType.sectionTitle, mb: 1.5 }}>
                    <Langs str="VLEARN_SETTINGS_DATA_GROUP" />
                </Typography>
                {/*
                  * The retention note is here, in full, rather than in a document nobody
                  * opens: this single number decides how long the platform keeps a record of
                  * which employee opened which lesson at which hour. An admin changing it
                  * should be able to see what it governs and why it has a limit without
                  * leaving the screen.
                  */}
                <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2,
                    backgroundColor: 'rgba(0, 40, 128, 0.04)',
                    border: '1px solid rgba(0, 40, 128, 0.08)',
                    borderRadius: '10px', px: 1.5, py: 1.25
                }}>
                    <InfoOutlinedIcon sx={{ fontSize: 17, color: 'var(--vital-blue, #002880)', mt: '1px' }}
                        aria-hidden="true" />
                    <Typography sx={{ ...vlearnType.body, lineHeight: 1.55 }}>
                        <Langs str="VLEARN_SETTING_RETENTION_HELP" />
                    </Typography>
                </Box>
                {numberField('activity_log_retention_days', 'VLEARN_SETTING_RETENTION_DAYS')}
            </Box>

            {/* ── Feature switches ────────────────────────────────────────────────── */}
            <Box component="section" sx={{ ...vlearnSurface.panel }}>
                <Typography component="h2" sx={{ ...vlearnType.sectionTitle, mb: 1.5 }}>
                    <Langs str="VLEARN_SETTINGS_FEATURES_GROUP" />
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.certificates_enabled}
                                onChange={(event) => setForm((current) => ({
                                    ...current, certificates_enabled: event.target.checked
                                }))}
                            />
                        }
                        label={<Langs str="VLEARN_SETTING_CERTIFICATES" />}
                    />
                    <FormHelperText sx={{ mt: 0, ml: 0 }}>
                        <Langs str="VLEARN_SETTING_CERTIFICATES_HELP" />
                    </FormHelperText>
                </Box>

                <Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.announcements_email_enabled}
                                onChange={(event) => setForm((current) => ({
                                    ...current, announcements_email_enabled: event.target.checked
                                }))}
                            />
                        }
                        label={<Langs str="VLEARN_SETTING_ANNOUNCEMENTS_EMAIL" />}
                    />
                    <FormHelperText sx={{ mt: 0, ml: 0 }}>
                        <Langs str="VLEARN_SETTING_ANNOUNCEMENTS_EMAIL_HELP" />
                    </FormHelperText>
                </Box>
            </Box>

            {/* ── Categories ──────────────────────────────────────────────────────── */}
            <Box component="section" sx={{ ...vlearnSurface.panel }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                        <Typography component="h2" sx={{ ...vlearnType.sectionTitle }}>
                            <Langs str="VLEARN_CATEGORIES_TITLE" />
                        </Typography>
                        <Typography sx={{ ...vlearnType.body, mt: 0.25 }}>
                            <Langs str="VLEARN_CATEGORIES_HELP" />
                        </Typography>
                    </Box>
                    {canEditCategories ? (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddRoundedIcon />}
                            onClick={() => setDraftCategory({
                                id: null, name: '', color: CATEGORY_COLORS[0], order: 0
                            })}
                            sx={{ textTransform: 'none' }}
                        >
                            <Langs str="VLEARN_CATEGORY_ADD" />
                        </Button>
                    ) : null}
                </Box>

                {!canEditCategories ? (
                    <FormHelperText sx={{ mt: 1 }}>
                        <Langs str="VLEARN_CATEGORIES_EDIT_ALL_REQUIRED" />
                    </FormHelperText>
                ) : null}

                <Divider sx={{ my: 2 }} />

                {(categories || []).length === 0 ? (
                    <VLearnEmptyState
                        icon={<LabelOutlinedIcon />}
                        title={<Langs str="VLEARN_NO_CATEGORIES_TITLE" />}
                        description={<Langs str="VLEARN_NO_CATEGORIES_BODY" />}
                    />
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(categories || []).map((category) => {
                            const count = Number(category.courses_count) || 0
                            return (
                                <Box
                                    key={category.id}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.25,
                                        border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px',
                                        px: 1.5, py: 1.1, flexWrap: 'wrap'
                                    }}
                                >
                                    {/* The swatch is decoration; the name beside it is the
                                        identity. Nothing here depends on telling two colours
                                        apart. */}
                                    <Box aria-hidden="true" sx={{
                                        width: 12, height: 12, borderRadius: '4px', flexShrink: 0,
                                        backgroundColor: category.color || 'var(--lightGrey, #ededed)',
                                        border: '1px solid rgba(0,0,0,0.12)'
                                    }} />
                                    <Typography sx={{ ...vlearnType.cardTitle, flex: 1, minWidth: 120 }}>
                                        {category.name}
                                    </Typography>
                                    <Typography sx={{
                                        ...vlearnType.meta, fontVariantNumeric: 'tabular-nums'
                                    }}>
                                        <Langs
                                            str={count === 1
                                                ? "VLEARN_CATEGORY_COURSES_ONE"
                                                : "VLEARN_CATEGORY_COURSES_MANY"}
                                            params={{ count }}
                                        />
                                    </Typography>
                                    {canEditCategories ? (
                                        // `gap: 0.25` was 2px between Rename and Deactivate —
                                        // one of which is destructive. 8px plus a 44px target
                                        // on each is the minimum that makes them separate
                                        // controls to a thumb rather than one wide one.
                                        <Box sx={{
                                            display: 'flex', gap: 1,
                                            '& .MuiIconButton-root': { minWidth: 44, minHeight: 44 }
                                        }}>
                                            <Tooltip title={Localize("VLEARN_CATEGORY_RENAME_ARIA", lang)} arrow>
                                                <IconButton
                                                    size="small"
                                                    aria-label={Localize("VLEARN_CATEGORY_RENAME_ARIA", lang)}
                                                    onClick={() => setDraftCategory({
                                                        id: category.id,
                                                        name: category.name || '',
                                                        color: category.color || CATEGORY_COLORS[0],
                                                        order: Number(category.order) || 0
                                                    })}
                                                >
                                                    <EditOutlinedIcon sx={{ fontSize: 17 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={Localize("VLEARN_CATEGORY_DEACTIVATE_ARIA", lang)} arrow>
                                                <IconButton
                                                    size="small"
                                                    aria-label={Localize("VLEARN_CATEGORY_DEACTIVATE_ARIA", lang)}
                                                    onClick={() => setPendingDeactivate(category)}
                                                >
                                                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ) : null}
                                </Box>
                            )
                        })}
                    </Box>
                )}
            </Box>

            {/* ── Category editor ─────────────────────────────────────────────────── */}
            <Dialog open={Boolean(draftCategory)} onClose={() => setDraftCategory(null)}
                fullWidth maxWidth="xs">
                <DialogTitle component="h2" sx={{ ...vlearnType.sectionTitle }}>
                    <Langs str={draftCategory && draftCategory.id
                        ? "VLEARN_CATEGORY_RENAME_ARIA" : "VLEARN_CATEGORY_ADD"} />
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label={Localize("VLEARN_CATEGORY_NAME", lang)}
                        value={(draftCategory && draftCategory.name) || ''}
                        onChange={(event) => setDraftCategory((current) => ({
                            ...current, name: event.target.value
                        }))}
                        // 125 is the column width; clipping here rather than letting the
                        // server truncate what was typed.
                        inputProps={{ maxLength: 125 }}
                        size="small"
                        fullWidth
                        autoFocus
                    />

                    <Box>
                        <Typography sx={{ ...vlearnType.meta, mb: 0.75 }}>
                            <Langs str="VLEARN_CATEGORY_COLOR" />
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {CATEGORY_COLORS.map((color) => {
                                const selected = draftCategory && draftCategory.color === color
                                return (
                                    <IconButton
                                        key={color}
                                        size="small"
                                        aria-label={color}
                                        aria-pressed={Boolean(selected)}
                                        onClick={() => setDraftCategory((current) => ({
                                            ...current, color
                                        }))}
                                        sx={{
                                            width: 30, height: 30, borderRadius: '8px',
                                            backgroundColor: color,
                                            border: selected
                                                ? '2px solid var(--text-primary, #111318)'
                                                : '1px solid rgba(0,0,0,0.12)',
                                            '&:hover': { backgroundColor: color },
                                            '&:focus-visible': {
                                                outline: '2px solid var(--text-primary, #111318)',
                                                outlineOffset: 2
                                            }
                                        }}
                                    >
                                        {/* The tick, not the border alone, is what says
                                            "selected" — a border colour on a coloured square
                                            is not a reliable signal. */}
                                        {selected
                                            ? <CheckRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
                                            : null}
                                    </IconButton>
                                )
                            })}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ gap: 1, px: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Button onClick={() => setDraftCategory(null)}
                        sx={{ textTransform: 'none', minHeight: 44 }}
                        startIcon={<CloseRoundedIcon />}>
                        <Langs str="VLEARN_CANCEL" />
                    </Button>
                    <Button variant="contained" onClick={onSaveCategory}
                        sx={{ textTransform: 'none', minHeight: 44 }}>
                        <Langs str="VLEARN_SAVE" />
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Deactivation confirmation ───────────────────────────────────────── */}
            <Dialog open={Boolean(pendingDeactivate)} onClose={() => setPendingDeactivate(null)}>
                <DialogTitle component="h2" sx={{ ...vlearnType.sectionTitle }}>
                    <Langs str="VLEARN_CATEGORY_DEACTIVATE_TITLE" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ ...vlearnType.body }}>
                        <Langs str="VLEARN_CATEGORY_DEACTIVATE_BODY" />
                    </DialogContentText>
                </DialogContent>
                {/* `gap: 2`: a destructive confirm must not share an edge with Cancel. */}
                <DialogActions sx={{ gap: 2, px: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Button onClick={() => setPendingDeactivate(null)}
                        sx={{ textTransform: 'none', minHeight: 44 }}>
                        <Langs str="VLEARN_CANCEL" />
                    </Button>
                    <Button color="error" variant="contained" onClick={onConfirmDeactivate}
                        sx={{ textTransform: 'none', minHeight: 44 }}>
                        <Langs str="VLEARN_DELETE" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
